<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useMemberService } from "@/stores/memberFunction/memberService";
import { useCounterStore } from "@/stores/counter";
import { useContentInfo } from "@/stores/contentInfo";
import { required } from "@vee-validate/rules";
import { useIsSubmitting, defineRule } from "vee-validate";

import SidebarWeb from "@/components/menberFunction/functionNavbar/sidebarWeb.vue";

import triangle_icon from "@image/memberFunction/common/triangle_icon.png";
import 文字框_長_A from "@image/memberFunction/文字框/文字框_長_A.png";

interface WithdrawPageFrom {
  withdrawal: string;
  withdrawal_Type: string;
  nMoney: string;
  sPassword: string;
}
const { t } = useI18n();
const router = useRouter();
const memberService = useMemberService();
const store = useCounterStore();
const contentInfo = useContentInfo();
const { MemberWithdrawPage, hasBankCard, WithdrawPageAlertContent } =
  storeToRefs(memberService);

const currentSelectCard = ref(0);
const withdrawAmount = ref(0);

const DispenseFee = computed(() => {
  let returnDispenseFee = "";
  if (!MemberWithdrawPage.value) return returnDispenseFee;
  if (!MemberWithdrawPage.value.setting_param) return returnDispenseFee;
  const mode = MemberWithdrawPage.value.setting_param.DispenseFeeMode;
  switch (mode) {
    case "0":
      returnDispenseFee = `${MemberWithdrawPage.value.setting_param.DispenseFeeFlat} 元`;
      break;
    case "1":
      returnDispenseFee =
        `${MemberWithdrawPage.value.setting_param.DispenseFeePer} %` +
        " + " +
        `${MemberWithdrawPage.value.setting_param.DispenseFeeFlat} 元`;
      break;
    default:
      returnDispenseFee = "";
      break;
  }
  return returnDispenseFee;
});

const WithdrawalsFrom = ref<any>(null);

async function submit(withdrawPageFrom: WithdrawPageFrom) {
  if (!hasBankCard.value) {
    store.toggleAlertBg();
    store.openWithdrawPageAlert();
    WithdrawPageAlertContent.value = t(
      "MemberFunction.Withdraw.BankCardNotFound"
    );
    return;
  }
  const { withdrawal, nMoney, sPassword } = withdrawPageFrom;

  const postData = {
    nTime: MemberWithdrawPage.value.setting_param.nTime.toString(),
    withdrawal: MemberWithdrawPage.value.user_bank[currentSelectCard.value].nId,
    withdrawal_Type: "1",
    nMoney: nMoney.toString(),
    sPassword,
  };
  await memberService.sendMemberWithdrawals(postData);
  WithdrawalsFrom.value.resetForm();
}

const gotoAddBank = () => {
  router.push({ name: "MemberBankPage" });
};

// 驗證提領金額尾數限制
function validateWithdrawalStint(value: number): string | null {
  if (!contentInfo.contentInfo.aWithdrawalStint) {
    return null; // 沒有限制規則
  }

  const stint = contentInfo.contentInfo.aWithdrawalStint;
  const valueStr = value.toString();
  const len = valueStr.length;

  // 獲取各位數字
  const singleDigit = len >= 1 ? valueStr[len - 1] : '0';
  const tenDigit = len >= 2 ? valueStr[len - 2] : '0';
  const hundredDigit = len >= 3 ? valueStr[len - 3] : '0';

  // 驗證個位數
  if (stint.sSingleDigits !== '*') {
    if (stint.sSingleDigits === '-0' && singleDigit === '0') {
      return t('MemberFunction.Deposit.SingleDigitCannotBeZero');
    } else if (stint.sSingleDigits.startsWith('+')) {
      const requiredDigit = stint.sSingleDigits.substring(1);
      if (singleDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.SingleDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  // 驗證十位數
  if (stint.sTens !== '*') {
    if (stint.sTens === '-0' && tenDigit === '0') {
      return t('MemberFunction.Deposit.TenDigitCannotBeZero');
    } else if (stint.sTens.startsWith('+')) {
      const requiredDigit = stint.sTens.substring(1);
      if (tenDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.TenDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  // 驗證百位數
  if (stint.sHundreds !== '*') {
    if (stint.sHundreds === '-0' && hundredDigit === '0') {
      return t('MemberFunction.Deposit.HundredDigitCannotBeZero');
    } else if (stint.sHundreds.startsWith('+')) {
      const requiredDigit = stint.sHundreds.substring(1);
      if (hundredDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.HundredDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  return null;
}

defineRule('unit_digit_zero', (value: number | string) => {
  const num = Number(value);
  // 使用动态验证函数
  const error = validateWithdrawalStint(num);
  if (error) {
    return error;
  }
  return true;
});

// 监听金额变化
watch(
  () => withdrawAmount.value,
  (newValue) => {
    const numValue = Number(newValue);
    if (!MemberWithdrawPage.value?.setting_param) return;
    const { LowestWithdraw } = MemberWithdrawPage.value.setting_param;
    
    // 检查最小值
    if (numValue < Number(LowestWithdraw)) {
      WithdrawalsFrom.value?.setFieldError("nMoney", t('MemberFunction.Withdraw.MinAmountError', { min: LowestWithdraw }));
      return;
    }
    
    // 检查尾数限制
    const stintError = validateWithdrawalStint(numValue);
    if (stintError) {
      WithdrawalsFrom.value?.setFieldError("nMoney", stintError);
    } else {
      WithdrawalsFrom.value?.setFieldError("nMoney", "");
    }
  },
);
</script>

<template>
  <div class="relative member-in-border w-full h-full py-[12px] px-[12px]">
    <button type="button" class="btn-y-border py-[.1em] px-[2em] relative left-1/2 -translate-x-1/2 text-secondary"
      v-if="!hasBankCard" @click="gotoAddBank">
      {{ $t("MemberFunction.Withdraw.GotoBind") }}
    </button>
    <VForm @submit="submit" ref="WithdrawalsFrom" v-slot="{ isSubmitting }">
      <div class="text-primary">
        <label for="" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[1vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.TimeForWithdraw") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.WithdrawStartTime
                : ""
            }}
            ~
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.WithdrawEndTime
                : ""
            }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.MaxAmountForWithdraw") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.TopWithdraw
                : ""
            }}
            {{ $t("MemberFunction.Withdraw.Yuan") }}
          </p>
        </label>

        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]" v-if="
          MemberWithdrawPage.setting_param &&
          MemberWithdrawPage.setting_param.FreeWithdrawTimes !== '99'
        ">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.WithdrawalFee") }}
          </p>
          <p class="col-span-5">{{ DispenseFee }}</p>
        </label>
        <!-- <label
          for="name"
          class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.WithdrawTime") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.nTime
                : ""
            }}
            {{ $t("MemberFunction.Withdraw.Time") }} /
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.WithdrawTimes
                : ""
            }}
            {{ $t("MemberFunction.Withdraw.Time") }}
          </p>
        </label> -->
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]" v-if="
          MemberWithdrawPage.setting_param &&
          MemberWithdrawPage.setting_param.FreeWithdrawTimes !== '99'
        ">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.WithdrawalFreeFeeTime") }}
          </p>

          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.FreeWithdrawTimes
                : "0"
            }}
            {{ $t("MemberFunction.Withdraw.Time") }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.WithdrawType") }}
          </p>
          <p class="col-span-5">{{ $t("MemberFunction.Withdraw.BankCard") }}</p>
        </label>
        <label for="withdrawal" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.BankCardNumber") }}
          </p>

          <div class="relative col-span-5 max-w-[300px] w-full">
            <div class="h-[30px] w-full">
              <img :src="triangle_icon" alt=""
                class="h-[12px] absolute right-[2.5%] md:right-[3.4%] top-[13px] -translate-y-1/2 z-20" />
            </div>
            <VField id="aTunnel" name="currentSelectCard" v-model="currentSelectCard" v-slot="{ field }"
              class="relative" :rules="{ required: true }">
              <select v-bind="field"
                class="absolute select-bg-border top-0 bg-transparent px-2 rounded w-full h-[30px] text-primary">
                <option class="text-black" v-for="(value, key) in MemberWithdrawPage.user_bank" :key="value.nId"
                  :value="key">
                  {{ value.sPayNum }}
                </option>
              </select>
            </VField>
          </div>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.AccountBank") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.user_bank
                ? MemberWithdrawPage.user_bank[currentSelectCard].sBank
                : ""
            }}
          </p>
        </label>
        <label for="bank" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.AccountBranch") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.user_bank
                ? MemberWithdrawPage.user_bank[currentSelectCard].sPayAddr
                : ""
            }}
          </p>
        </label>
        <label for="nMoney" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.WithdrawAmount") }}
          </p>
          <div class="col-span-5 md:col-span-2">
            <div class="relative">
              <div class="h-[26px]">
                <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
              </div>
              <VField class="absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2"
                :label="$t('MemberFunction.Withdraw.WithdrawAmount')" name="nMoney" type="number"
                @input="withdrawAmount = Number($event.target.value)"
                :min="MemberWithdrawPage.setting_param
                  ? MemberWithdrawPage.setting_param.LowestWithdraw
                  : 0
                  " :rules="{
                    required: true,
                    min_value: MemberWithdrawPage.setting_param
                      ? MemberWithdrawPage.setting_param.LowestWithdraw
                      : 0,
                    unit_digit_zero: true
                  }" />
            </div>
            <ErrorMessage name="nMoney" class="text-red-400 text-[12px] text-center static lg:absolute" />
          </div>
        </label>
        <label for="sPassword" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.CheckPassword") }}
          </p>
          <div class="col-span-5 md:col-span-2">
            <div class="relative">
              <div class="h-[26px]">
                <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
              </div>
              <VField class="absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2"
                :label="t('MemberFunction.Withdraw.CheckPassword')" name="sPassword" type="password" :min="MemberWithdrawPage.setting_param
                  ? MemberWithdrawPage.setting_param.LowestWithdraw
                  : 0
                  " :rules="{ required: true }" />
            </div>
            <ErrorMessage name="sPassword" class="text-red-400 text-[12px] text-center static lg:absolute" />
          </div>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.WageringLimit") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.nAccess
                : ""
            }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.RequiredWageringAmount") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.nAccessMin
                : ""
            }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.MinimumWithdrawal") }}
          </p>
          <p class="col-span-5">
            {{
              MemberWithdrawPage.setting_param
                ? MemberWithdrawPage.setting_param.LowestWithdraw
                : ""
            }}
            {{ $t("MemberFunction.Withdraw.Yuan") }}
          </p>
        </label>
      </div>
      <div
        class="absolute lg:static bottom-[-12%] lg:bottom-[0] left-1/2 mt-[2em] -translate-x-1/2 lg:translate-x-0 flex items-center justify-center">
        <button type="submit" class="btn-y-border" :class="{ submitting: isSubmitting }" :disabled="isSubmitting">
          <p class="text-[12px] md:text-[18px] text-secondary px-[1.2em] py-[.1em] z-10">
            {{ isSubmitting ? "" : $t("MemberFunction.Withdraw.Next") }}
          </p>
        </button>
      </div>
    </VForm>
  </div>
</template>

<style>
.bg-100 {
  background-size: 100% 100%;
}
</style>
